import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Create axios instance for authenticated requests
export const axiosAuth = axios.create({
  baseURL: API,
});

// Add Authorization header to authenticated requests
axiosAuth.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const formatApiError = (error) => {
    const detail = error?.response?.data?.detail;
    if (detail == null) {
      // No response reached us — network error, CORS block, or unreachable backend.
      if (error?.message === 'Network Error' || !error?.response) {
        return `Cannot reach backend (${API || 'no URL configured'}). Check internet or contact support.`;
      }
      return `Error ${error?.response?.status || '??'}: ${error?.message || 'Unknown error'}`;
    }
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axiosAuth.get('/api/auth/me');
      setUser(data);
    } catch (e) {
      localStorage.removeItem('access_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, recaptchaToken = null) => {
    try {
      const payload = { email, password };
      if (recaptchaToken) payload.recaptcha_token = recaptchaToken;
      const { data } = await axios.post(`${API}/api/auth/login`, payload);
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/register`, { email, password, name });
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`);
      localStorage.removeItem('access_token');
      setUser(false);
    } catch (e) {
      localStorage.removeItem('access_token');
      setUser(false);
    }
  };

  const value = React.useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    isAdmin: user?.role === 'admin'
  }), [user, loading, login, register, logout, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
